import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import CatalogImage from '../components/silvr/CatalogImage';
import type { Show } from '../shows';
import { confirmedProductFor } from '../shows/confirmed';
import {
  Candidate,
  ClipSummary,
  CurationApi,
  CurationClip,
  CurationTrack,
  FILTER_LABEL,
  PublishResult,
  Selections,
  TrackSelection,
  shortCategory,
  trackColor,
  trackPositionAt,
  isUnconfirmed,
  getPublishedClip,
  PublishedClip,
  CURATION_PASSCODE_KEY,
} from '../utils/clipApi';
import '../components/silvr/silvr.scss';
import './curate.scss';

// Shared with the board's Viewers panel, so one passcode opens both for the tab.
const PASSCODE_KEY = CURATION_PASSCODE_KEY;

const formatTime = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds - m * 60;
  return `${m}:${s.toFixed(1).padStart(4, '0')}`;
};

type Status = 'chosen' | 'hidden' | 'open';

const statusOf = (selection: TrackSelection | undefined): Status => {
  if (selection?.hidden) return 'hidden';
  if (selection?.chosen !== null && selection?.chosen !== undefined) return 'chosen';
  return 'open';
};

// The hotspot is filled with the garment's colour (the same as its mask overlay); the ring says the status.
const STATUS_RING: Record<Status, string> = {
  chosen: '3px solid #10b981',
  hidden: '3px dashed #9ca3af',
  open: '2px solid #ffffff',
};

const colorOf = (track: { id: number; color?: string }) => track.color || trackColor(track.id);

// --- login ---------------------------------------------------------------------------------------

const PasscodeGate: React.FC<{ error: string | null; onSubmit: (passcode: string) => void }> = ({ error, onSubmit }) => {
  const [value, setValue] = useState('');
  return (
    <main className="curate-login silvr-ui">
      <form
        className="curate-login__card"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(value.trim());
        }}
      >
        <img src="/silvr-logo.png" alt="Silvr" className="curate-login__logo" />
        <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">Silvr · Curation</p>
        <h1 className="mt-1 text-2xl font-semibold">Pick the products clients will see</h1>
        <p className="mt-2 text-sm text-gray-500">Enter the curation passcode. It is sent with every change and never stored in the site.</p>
        <label className="mt-6 block text-xs font-semibold uppercase tracking-wider text-gray-500" htmlFor="curation-passcode">
          Passcode
        </label>
        <input
          id="curation-passcode"
          type="password"
          autoComplete="off"
          className="mt-1 w-full rounded-xl border border-gray-300 px-4 py-3 text-base"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          required
        />
        {error && (
          <p role="alert" className="mt-3 text-sm font-medium text-red-600">
            {error}
          </p>
        )}
        <button type="submit" className="mt-6 min-h-12 w-full rounded-xl bg-gray-950 text-base font-medium text-white">
          Open curation
        </button>
      </form>
    </main>
  );
};

// --- candidate card --------------------------------------------------------------------------------

const CandidateCard: React.FC<{
  candidate: Candidate;
  chosen: boolean;
  alternate: boolean;
  busy: boolean;
  onChoose: () => void;
  onToggleAlternate: () => void;
}> = ({ candidate, chosen, alternate, busy, onChoose, onToggleAlternate }) => (
  <article className={`flex flex-col overflow-hidden rounded-2xl border bg-white ${chosen ? 'border-emerald-500 ring-2 ring-emerald-200' : alternate ? 'border-gray-900' : 'border-gray-200'}`}>
    <div className="relative aspect-[4/5] bg-gray-100 p-2">
      <CatalogImage src={candidate.image_url ?? null} alt={candidate.name || 'candidate'} className="h-full w-full object-contain" />
      <span className="absolute left-2 top-2 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-semibold text-white">#{candidate.rank}</span>
      {chosen && <span className="absolute right-2 top-2 rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-semibold text-white">Chosen</span>}
      {!chosen && alternate && <span className="absolute right-2 top-2 rounded-full bg-gray-900 px-2 py-0.5 text-[10px] font-semibold text-white">Similar</span>}
    </div>
    <div className="flex flex-1 flex-col px-3 pt-2">
      <p className="truncate text-[10px] font-semibold uppercase tracking-widest text-gray-500" title={candidate.brand || ''}>
        {candidate.brand || candidate.merchant || 'Unknown brand'}
      </p>
      <p className="mt-1 line-clamp-2 min-h-10 text-xs font-medium leading-5" title={candidate.name || ''}>
        {candidate.name || 'Untitled listing'}
      </p>
      <p className="mt-1 text-[11px] text-gray-500">
        {shortCategory(candidate.category)} · {candidate.merchant} · score {candidate.score?.toFixed(3)} · {candidate.hits}/3 queries
      </p>
    </div>
    <div className="flex items-center gap-2 px-3 pb-3 pt-2">
      <button
        type="button"
        disabled={busy}
        onClick={onChoose}
        className={`min-h-9 flex-1 rounded-full px-3 text-xs font-medium ${chosen ? 'bg-emerald-500 text-white' : 'bg-gray-950 text-white'} disabled:opacity-50`}
      >
        {chosen ? 'Chosen ✓' : 'Choose'}
      </button>
      <button
        type="button"
        disabled={busy || chosen}
        onClick={onToggleAlternate}
        className={`min-h-9 rounded-full border px-3 text-xs font-medium ${alternate ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200'} disabled:opacity-40`}
        title="Show as a similar item"
      >
        {alternate ? 'Similar ✓' : 'Similar'}
      </button>
    </div>
  </article>
);

// --- the page ------------------------------------------------------------------------------------

/** The curation page for one show: its clips (the show's slugs), one garment at a time. */
const Curate: React.FC<{ show: Show }> = ({ show }) => {
  const [passcode, setPasscode] = useState<string>(() => {
    try {
      return sessionStorage.getItem(PASSCODE_KEY) || '';
    } catch {
      return '';
    }
  });
  const [loginError, setLoginError] = useState<string | null>(null);
  const [clips, setClips] = useState<ClipSummary[] | null>(null);
  const [slug, setSlug] = useState<string | null>(null);
  const [clip, setClip] = useState<CurationClip | null>(null);
  // what clients see right now (the last publish), to say plainly when this run is not live yet
  const [live, setLive] = useState<PublishedClip | null | undefined>(undefined);
  const refreshLive = () => {
    if (!slug) return;
    getPublishedClip(slug)
      .then((published) => setLive(published))
      .catch(() => setLive(null));
  };
  const [selections, setSelections] = useState<Selections | null>(null);
  const [selectedTrackId, setSelectedTrackId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [publishResult, setPublishResult] = useState<PublishResult | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Follow every rendered frame, not the ~4 Hz `timeupdate`: requestVideoFrameCallback hands over the
  // exact media time of the frame on screen. Fallback: a rAF loop while playing.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    let raf = 0;
    let stopped = false;
    const v = video as HTMLVideoElement & { requestVideoFrameCallback?: (cb: (now: number, meta: { mediaTime: number }) => void) => number };
    const tick = () => {
      if (stopped) return;
      if (v.requestVideoFrameCallback) {
        v.requestVideoFrameCallback((_now, meta) => {
          setCurrentTime(meta.mediaTime);
          if (!video.paused && !video.ended) tick();
        });
      } else {
        setCurrentTime(video.currentTime);
        if (!video.paused && !video.ended) raf = requestAnimationFrame(tick);
      }
    };
    const onPlay = () => {
      setPlaying(true);
      tick();
    };
    const onPause = () => {
      setPlaying(false);
      setCurrentTime(video.currentTime);
    };
    const onSeeked = () => {
      setCurrentTime(video.currentTime);
      if (!video.paused) tick();
    };
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onPause);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('loadedmetadata', onSeeked);
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onPause);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('loadedmetadata', onSeeked);
    };
  }, [clip?.slug]);
  const api = useMemo(() => (passcode ? new CurationApi(passcode) : null), [passcode]);

  // login -> clip list
  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    api
      .listClips()
      .then((all) => {
        if (cancelled) return;
        // only this show's clips; a slug the pipeline has not produced yet is listed as pending
        const list = show.slugs.map(
          (s) => all.find((c) => c.slug === s) ?? { slug: s, generated_at: '', source: { file: '', fps: 0, n_frames: 0, width: 0, height: 0, duration: 0 }, n_tracks: 0, n_decided: 0, updated_at: null, published_at: null },
        );
        setClips(list);
        setLoginError(null);
        try {
          sessionStorage.setItem(PASSCODE_KEY, passcode);
        } catch {
          /* private mode */
        }
        if (list.length && !slug) setSlug(list[0].slug);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setLoginError(err.message);
        setPasscode('');
        try {
          sessionStorage.removeItem(PASSCODE_KEY);
        } catch {
          /* ignore */
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const loadClip = useCallback(() => {
    if (!api || !slug) return;
    api
      .getClip(slug)
      .then(({ clip: data, selections: sel }) => {
        setClip(data);
        setSelections(sel);
        setSelectedTrackId((current) => current ?? data.tracks[0]?.id ?? null);
        refreshLive();
      })
      .catch((err: Error) => setMessage(err.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, slug]);

  useEffect(() => {
    setClip(null);
    setSelectedTrackId(null);
    loadClip();
  }, [loadClip]);

  const allTracks = useMemo(() => (clip ? [...clip.tracks].sort((a, b) => a.start_frame - b.start_frame || a.id - b.id) : []), [clip]);
  const fps = clip?.source.fps || 24;
  const duration = clip?.source.duration || 1;
  const frame = Math.round(currentTime * fps);
  const [showMasks, setShowMasks] = useState(true);
  // Garments the pipeline filtered (low confidence, small) and garments an admin removed stay out of the
  // way unless this is on; a filtered garment with a chosen product always shows.
  const [showAll, setShowAll] = useState(false);
  const selectionFor = (track: CurationTrack): TrackSelection | undefined => selections?.tracks[String(track.id)];
  // A product a person confirmed for the garment in the show's config (src/shows/<key>/products.ts):
  // clients see it instead of the catalog choice below, so the page says so.
  const confirmedFor = (track: CurationTrack) => (show.confirmed ? confirmedProductFor(show.confirmed, track) : null);
  const isFiltered = (track: CurationTrack) => Boolean(track.filtered?.length);
  const tracks = useMemo(
    () => allTracks.filter((t) => showAll || (selectionFor(t)?.chosen != null && !selectionFor(t)?.hidden) || (!isFiltered(t) && !selectionFor(t)?.hidden)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allTracks, showAll, selections],
  );
  const overlayFrames = useMemo(() => new Set((showAll ? clip?.overlay_all_frames : clip?.overlay_frames) ?? []), [clip, showAll]);
  const overlayBase = showAll ? clip?.overlay_all_base : clip?.overlay_base;
  // One PNG per frame over the network cannot keep up with playback; masks are for inspecting a paused
  // frame, so they render when paused (hotspots keep following the frames while playing).
  const overlayUrl = !playing && overlayBase && overlayFrames.has(frame) ? `${overlayBase}f${frame}.png` : null;
  useEffect(() => {
    if (playing || !overlayBase) return;
    // warm the neighbouring frames so stepping with the arrow keys shows the mask at once
    for (const d of [1, -1, 2, -2, 3, -3]) {
      const f = frame + d;
      if (overlayFrames.has(f)) new Image().src = `${overlayBase}f${f}.png`;
    }
  }, [frame, playing, overlayBase, overlayFrames]);
  const selectedTrack = allTracks.find((t) => t.id === selectedTrackId) ?? null;
  const defaultTracks = allTracks.filter((t) => !isFiltered(t));
  const decided = defaultTracks.filter((t) => statusOf(selectionFor(t)) !== 'open').length;
  const nFiltered = allTracks.length - defaultTracks.length;
  const nRemoved = allTracks.filter((t) => selectionFor(t)?.hidden).length;
  const nConfirmed = allTracks.filter((t) => confirmedFor(t)).length;

  const seekTo = (seconds: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    video.currentTime = seconds;
    setCurrentTime(seconds);
  };

  const selectTrack = (track: CurationTrack, seek = true) => {
    setSelectedTrackId(track.id);
    if (seek) {
      const query = track.queries[Math.floor(track.queries.length / 2)];
      seekTo(query ? query.t : track.start_t);
    }
  };

  const stepTrack = (delta: number) => {
    if (!tracks.length) return;
    const index = tracks.findIndex((t) => t.id === selectedTrackId);
    const next = tracks[(index + delta + tracks.length) % tracks.length];
    selectTrack(next);
  };

  // Garments a client would see whose product nobody has looked at (the API counts the same way).
  const unconfirmed = defaultTracks.filter((track) => isUnconfirmed(selectionFor(track)));

  const goToNextUnconfirmed = () => {
    if (unconfirmed.length === 0) return;
    const after = unconfirmed.find((track) => track.id > (selectedTrackId ?? -1));
    selectTrack(after ?? unconfirmed[0]);
  };

  const save = async (track: CurationTrack, patch: Partial<TrackSelection>) => {
    if (!api || !slug) return;
    const current = selectionFor(track) ?? { chosen: null, alternates: [], hidden: false, note: '' };
    const next = { chosen: current.chosen, alternates: current.alternates, hidden: current.hidden, note: current.note, ...patch };
    setBusy(true);
    setMessage(null);
    try {
      setSelections(await api.saveTrack(slug, track.id, next));
    } catch (err) {
      setMessage(`Could not save: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (track: CurationTrack) => {
    if (!api || !slug) return;
    setBusy(true);
    setMessage(null);
    try {
      setSelections(await api.confirmTrack(slug, track.id));
    } catch (err) {
      setMessage(`Could not confirm: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!api || !slug) return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await api.publish(slug);
      setPublishResult(result);
      setSelections((prev) => (prev ? { ...prev, published_at: result.published_at } : prev));
      refreshLive();
    } catch (err) {
      setMessage(`Could not publish: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  // keyboard: j/k next/previous garment, 1-9 choose the nth candidate, h hide
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.target as HTMLElement).tagName === 'INPUT' || !selectedTrack) return;
      if (event.key === 'c' && selectedTrack && isUnconfirmed(selectionFor(selectedTrack))) confirm(selectedTrack);
      else if (event.key === 'u') goToNextUnconfirmed();
      else if (event.key === 'j') stepTrack(1);
      else if (event.key === 'k') stepTrack(-1);
      else if (event.key === 'h') save(selectedTrack, { hidden: !selectionFor(selectedTrack)?.hidden });
      else if (/^[1-9]$/.test(event.key)) {
        const candidate = selectedTrack.candidates[Number(event.key) - 1];
        if (candidate) save(selectedTrack, { chosen: candidate.gallery_row, hidden: false });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTrack, selections, tracks]);

  if (!passcode) return <PasscodeGate error={loginError} onSubmit={setPasscode} />;

  return (
    <main className="curate silvr-ui">
      <header className="curate__bar">
        <div className="flex items-center gap-3">
          <Link to="/" title="Back to the board">
            <img src="/silvr-logo.png" alt="Silvr" className="h-6" />
          </Link>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">
              Curation · {show.name} · <Link to={`/${show.key}`} className="underline">preview</Link>
            </p>
            <h1 className="text-base font-semibold leading-tight">{clip ? clip.slug : 'Loading…'}</h1>
            {clip && (
              <p className="text-xs text-gray-500">
                {clip.source.n_frames.toLocaleString()} frames · {clip.source.fps.toFixed(3)} fps · {clip.source.duration.toFixed(1)} s ·{' '}
                {clip.source.width}×{clip.source.height} · frame <strong className="text-gray-900">{frame}</strong> at {formatTime(currentTime)}
                {clip.filters && (
                  <>
                    {' '}· detector ≥ {clip.filters.conf}
                    {clip.filters.min_area_frac > 0 && <> · area ≥ {(clip.filters.min_area_frac * 100).toFixed(0)}%</>}
                    {clip.filters.engulf > 0 && <> · engulfed ≥ {(clip.filters.engulf * 100).toFixed(0)}% dropped</>}
                    {(clip.filters.unify ?? 0) > 0 && <> · boxes overlapping ≥ {((clip.filters.unify ?? 0) * 100).toFixed(0)}% unified</>}
                    {' '}· {clip.filters.kept_detections.toLocaleString()} of {clip.filters.raw_detections.toLocaleString()} detections kept
                  </>
                )}
              </p>
            )}
          </div>
          {clips && clips.length > 1 && (
            <select className="ml-4 rounded-lg border border-gray-300 px-2 py-1 text-sm" value={slug ?? ''} onChange={(event) => setSlug(event.target.value)}>
              {clips.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.slug} ({c.n_decided}/{c.n_tracks})
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-gray-600">
            <strong className="text-gray-900">{decided}</strong> of {defaultTracks.length} garments decided
            {nFiltered > 0 && <span className="ml-2 text-gray-400">· {nFiltered} low-confidence</span>}
            {nRemoved > 0 && <span className="ml-2 text-gray-400">· {nRemoved} removed</span>}
            {nConfirmed > 0 && <span className="ml-2 text-emerald-700">· {nConfirmed} confirmed by hand</span>}
          </span>
          {unconfirmed.length > 0 ? (
            <button
              type="button"
              onClick={goToNextUnconfirmed}
              className="min-h-9 rounded-full bg-amber-100 px-3 text-xs font-semibold text-amber-900"
              title="Publish chose these products, nobody has confirmed them. Click to go to the next one (u)."
            >
              {unconfirmed.length} unconfirmed
            </button>
          ) : (
            <span className="text-xs text-emerald-700" title="Every garment a client sees has a product a person chose or confirmed.">
              All confirmed
            </span>
          )}
          <span className="text-xs text-gray-500" title="What the client page serves right now. A rerun does not change it: only Publish does, and Publish carries the garments clients already see onto this run unless you chose or removed them.">
            {live === undefined && 'Checking what clients see…'}
            {live === null && 'Clients see nothing yet (never published)'}
            {live && clip && (
              <>
                Clients see {live.tracks.length} garment{live.tracks.length === 1 ? '' : 's'} published {new Date(live.published_at).toLocaleString()}
                {/* An API older than silvr-api-endpoint #113 does not record which run a publish came from.
                    Saying "not live yet" then would be a guess, so the line says what it knows instead. */}
                {!live.run_generated_at && ' (this publish does not say which run it came from)'}
                {live.run_generated_at === clip.generated_at && ' from this run'}
                {live.run_generated_at && live.run_generated_at !== clip.generated_at && (
                  <>
                    {' from an earlier run'}
                    <strong className="ml-1 text-amber-700">· this run is not live yet</strong>
                  </>
                )}
              </>
            )}
          </span>
          <button type="button" disabled={busy || !decided} onClick={publish} className="min-h-10 rounded-full bg-gray-950 px-5 text-sm font-medium text-white disabled:opacity-40">
            Publish to silvr.shop
          </button>
          <button
            type="button"
            onClick={() => {
              setPasscode('');
              try {
                sessionStorage.removeItem(PASSCODE_KEY);
              } catch {
                /* ignore */
              }
            }}
            className="text-sm text-gray-500 underline"
          >
            Lock
          </button>
        </div>
      </header>

      {(message || publishResult) && (
        <div className={`curate__notice ${message ? 'is-error' : ''}`} role="status">
          {message ??
            `Published ${publishResult?.n_tracks} garments and ${publishResult?.n_images} product photos. The client page picks it up within a minute.`}
          <button type="button" className="ml-3 underline" onClick={() => { setMessage(null); setPublishResult(null); }}>
            Dismiss
          </button>
        </div>
      )}

      {!clip ? (
        <p className="p-8 text-sm text-gray-500">Loading the clip and its candidates…</p>
      ) : (
        <div className="curate__layout">
          {/* --- left: video, hotspots of every track, timeline ----------------------------------- */}
          <section className="curate__video">
            <div className="curate__player">
              <video
                ref={videoRef}
                src={clip.video_url}
                controls
                playsInline
                preload="metadata"
              />
              <div className="curate__hotspots">
                {showMasks && overlayUrl && <img className="curate__overlay" src={overlayUrl} alt="" draggable={false} />}
                {tracks.map((track) => {
                  const position = trackPositionAt(track, frame);
                  if (!position) return null;
                  const status = statusOf(selectionFor(track));
                  return (
                    <button
                      key={track.id}
                      type="button"
                      className={`curate__hotspot ${track.id === selectedTrackId ? 'is-selected' : ''}`}
                      style={{ left: `${position.x}%`, top: `${position.y}%`, background: colorOf(track), border: STATUS_RING[status] }}
                      onClick={() => selectTrack(track, false)}
                      title={`Garment ${track.id}: ${status}${confirmedFor(track) ? ` · confirmed: ${confirmedFor(track)?.brand} ${confirmedFor(track)?.name}` : ''}`}
                    >
                      {track.id}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="curate__timeline" aria-label="Garment timeline">
              <div className="curate__playhead" style={{ left: `${(currentTime / duration) * 100}%` }} />
              {tracks.map((track) => {
                const status = statusOf(selectionFor(track));
                const fps = clip?.source.fps ?? 24;
                const segments: [number, number][] = track.segments && track.segments.length > 0 ? track.segments : [[track.start_frame, track.end_frame]];
                // One bar per segment: a garment that returns after a cut-away is one garment on several bars.
                return segments.map(([first, last], index) => (
                  <button
                    key={`${track.id}-${first}`}
                    type="button"
                    className={`curate__span curate__span--${status} ${isFiltered(track) ? 'is-filtered' : ''} ${isUnconfirmed(selectionFor(track)) ? 'is-unconfirmed' : ''} ${track.id === selectedTrackId ? 'is-selected' : ''}`}
                    style={{
                      left: `${(first / fps / duration) * 100}%`,
                      width: `${Math.max(0.4, ((last + 1 - first) / fps / duration) * 100)}%`,
                      background: colorOf(track),
                      opacity: status === 'hidden' ? 0.35 : isFiltered(track) && status === 'open' ? 0.55 : 1,
                    }}
                    onClick={() => selectTrack(track)}
                    title={`Garment ${track.id}${segments.length > 1 ? ` (part ${index + 1} of ${segments.length})` : ''} · ${formatTime(first / fps)}–${formatTime((last + 1) / fps)} · ${status}${isUnconfirmed(selectionFor(track)) ? ' (unconfirmed)' : ''}`}
                  >
                    <span>{track.id}</span>
                  </button>
                ));
              })}
            </div>
            <p className="mt-2 flex flex-wrap items-center gap-x-3 text-xs text-gray-500">
              <span>
                Each bar is one garment track, in its colour; the same colour tints the garment's pixels on the video. Green ring = chosen, dashed = removed, faded bar = low confidence. Keys: <kbd>j</kbd>/<kbd>k</kbd> next/previous, <kbd>1</kbd>–<kbd>9</kbd> choose that candidate, <kbd>h</kbd> remove/restore.
              </span>
              <label className="inline-flex items-center gap-1">
                <input type="checkbox" checked={showMasks} onChange={(event) => setShowMasks(event.target.checked)} /> show masks
              </label>
              <label className="inline-flex items-center gap-1" title="Garments the pipeline filtered (low confidence, small) and garments you removed. Pick a product for one and it is published like any other.">
                <input type="checkbox" checked={showAll} onChange={(event) => setShowAll(event.target.checked)} /> show low-confidence and removed garments
              </label>
            </p>
          </section>

          {/* --- right: the selected track and its candidates ------------------------------------- */}
          <section className="curate__panel">
            {selectedTrack ? (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-gray-500">
                      <span className="inline-block h-3 w-3 rounded-full" style={{ background: colorOf(selectedTrack) }} aria-hidden="true" />
                      Garment {selectedTrack.id} · {selectedTrack.shots && selectedTrack.shots.length > 1 ? `shots ${selectedTrack.shots.join(', ')}` : `shot ${selectedTrack.shot}`}
                      {selectedTrack.filtered?.map((reason) => (
                        <span key={reason} className="rounded-full bg-amber-100 px-2 py-0.5 normal-case tracking-normal text-amber-800">
                          {FILTER_LABEL[reason] ?? reason}
                        </span>
                      ))}
                      {selectionFor(selectedTrack)?.hidden && <span className="rounded-full bg-gray-200 px-2 py-0.5 normal-case tracking-normal text-gray-700">removed</span>}
                    </p>
                    <h2 className="text-lg font-semibold">
                      {formatTime(selectedTrack.start_t)} – {formatTime(selectedTrack.end_t)}
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        {selectedTrack.segments && selectedTrack.segments.length > 1
                          ? `${selectedTrack.segments.length} appearances: ${selectedTrack.segments.map(([a, b]) => `${a}–${b}`).join(', ')}`
                          : `frames ${selectedTrack.start_frame}–${selectedTrack.end_frame}`}{' '}
                        ({selectedTrack.n_det} detected) · detector {selectedTrack.max_score.toFixed(2)} ·{' '}
                        {selectionFor(selectedTrack)?.auto && <span className="text-amber-700">auto-chosen on publish (best candidate), not confirmed · </span>}
                        {selectionFor(selectedTrack)?.carried_from && !selectionFor(selectedTrack)?.auto && <span className="text-amber-700">carried from the previous publish, not confirmed · </span>}
                        {selectionFor(selectedTrack)?.confirmed_at && <span className="text-emerald-700">confirmed · </span>}
                        {(selectedTrack.mean_area_frac * 100).toFixed(1)}% of frame
                      </span>
                    </h2>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button type="button" onClick={() => stepTrack(-1)} className="min-h-9 rounded-full border border-gray-200 px-3 text-xs font-medium" title="Previous (k)">
                      ‹ Prev
                    </button>
                    <button type="button" onClick={() => stepTrack(1)} className="min-h-9 rounded-full border border-gray-200 px-3 text-xs font-medium" title="Next (j)">
                      Next ›
                    </button>
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  {selectedTrack.queries.map((query) => (
                    <figure key={query.frame} className="curate__query-tile">
                      <button type="button" className="curate__query" onClick={() => seekTo(query.t)} title={`Query frame ${query.frame} at ${formatTime(query.t)} (detector ${query.score.toFixed(2)})`}>
                        <img src={query.thumb_url} alt={`Query crop at ${formatTime(query.t)}`} />
                      </button>
                      <figcaption className="text-[10px] text-gray-500">
                        {formatTime(query.t)} · sharpness {query.sharpness !== undefined ? query.sharpness.toFixed(0) : '–'}
                      </figcaption>
                    </figure>
                  ))}
                  <div className="ml-auto flex gap-2">
                    {isUnconfirmed(selectionFor(selectedTrack)) && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => confirm(selectedTrack)}
                        className="min-h-9 rounded-full bg-amber-500 px-3 text-xs font-semibold text-white"
                        title="Publish chose this product; confirm that it is right (c). Choosing another product confirms it too."
                      >
                        Confirm
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => save(selectedTrack, { hidden: !selectionFor(selectedTrack)?.hidden })}
                      className={`min-h-9 rounded-full border px-3 text-xs font-medium ${selectionFor(selectedTrack)?.hidden ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200'}`}
                      title="A removed garment is not published and leaves the video and timeline (find it again with 'show low-confidence and removed garments')"
                    >
                      {selectionFor(selectedTrack)?.hidden ? 'Restore garment' : 'Remove garment'}
                    </button>
                    <button
                      type="button"
                      disabled={busy || selectionFor(selectedTrack)?.chosen == null}
                      onClick={() => save(selectedTrack, { chosen: null, alternates: [] })}
                      className="min-h-9 rounded-full border border-gray-200 px-3 text-xs font-medium disabled:opacity-40"
                    >
                      Clear choice
                    </button>
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  The crops above are what the model searched ({clip.model.models}, top {clip.model.k} per crop;{' '}
                  {clip.model.confidence ? 'confidence is the sharpness-weighted mean of the crops’ scores, so the crispest crop counts most' : 'merged by best score'}).
                  Choose the product clients will see; mark extra ones as Similar.
                </p>

                {(() => {
                  const product = confirmedFor(selectedTrack);
                  if (!product) return null;
                  return (
                    <aside className="mt-4 flex items-center gap-3 rounded-2xl border border-emerald-500 bg-emerald-50 p-3 ring-2 ring-emerald-200" aria-label="Confirmed product">
                      <CatalogImage src={product.image_url} alt={product.name || 'confirmed product'} className="h-20 w-16 shrink-0 rounded-lg bg-white object-contain" />
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-emerald-800">Confirmed by hand · clients see this</p>
                        <p className="truncate text-sm font-medium">
                          {product.brand} · {product.name}
                          {product.color ? ` · ${product.color}` : ''}
                        </p>
                        <p className="text-xs text-gray-600">
                          {product.links?.length ? `${product.links.length} listing${product.links.length === 1 ? '' : 's'}${product.price ? ` · ${product.price}` : ''}` : 'Shop at store'} · set in src/shows/{show.key}/products.ts; the catalog choice below is the fallback
                        </p>
                      </div>
                    </aside>
                  );
                })()}

                <div className="mt-4 grid grid-cols-2 gap-3 xl:grid-cols-3">
                  {selectedTrack.candidates.map((candidate) => {
                    const selection = selectionFor(selectedTrack);
                    const chosen = selection?.chosen === candidate.gallery_row;
                    const alternate = Boolean(selection?.alternates.includes(candidate.gallery_row));
                    return (
                      <CandidateCard
                        key={candidate.gallery_row}
                        candidate={candidate}
                        chosen={chosen}
                        alternate={alternate}
                        busy={busy}
                        onChoose={() => save(selectedTrack, { chosen: candidate.gallery_row, hidden: false, alternates: (selection?.alternates ?? []).filter((row) => row !== candidate.gallery_row) })}
                        onToggleAlternate={() =>
                          save(selectedTrack, {
                            alternates: alternate ? (selection?.alternates ?? []).filter((row) => row !== candidate.gallery_row) : [...(selection?.alternates ?? []), candidate.gallery_row],
                          })
                        }
                      />
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-500">Select a garment on the video or the timeline.</p>
            )}
          </section>
        </div>
      )}
    </main>
  );
};

export default Curate;
